<template>
  <div class="instrument-sidebar__container">
    <div>
      <Tabs
        v-if="action === OrderSteps.Confirmation || action === OrderSteps.Form"
        :style="computedOrderFormStyle"
        :selected-index="currentTabIndex"
        :on-change="tab => (currentTabIndex = tab)"
      >
        <TabList class="tabs">
          <Tab>Comprar</Tab>
          <Tab>Vender</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <div data-testid="buy-order-tab">
              <Order
                :type="OrderType.Buy"
                :price-market="bestBuyPrice"
                :lot-size="lotSize"
                :instrument-title="instrumentTitle"
                :status="status"
                :can-operate="canOperate"
                :instrument-status="instrumentStatus"
                :instrument="instrument"
                :quantity-instrument="quantityInstrument"
                :is-customer-blocked="isCustomerBlocked"
                :description="description"
                :loading-button="loadingOrder"
                @on-accept-risk-terms="emitAcceptRiskTerms"
                @confirm-order="confirmOrder"
                @load-available-balance-unified-account="
                  onLoadUserAvailableBalanceUnifiedAccount
                "
                @on-order-button-disabled="sendOrderButtonDisabledLog"
              />
            </div>
          </TabPanel>
          <TabPanel>
            <div data-testid="sell-order-tab">
              <Order
                :type="OrderType.Sell"
                :price-market="bestSellPrice"
                :lot-size="lotSize"
                :instrument-title="instrumentTitle"
                :quantity-instrument="quantityInstrument"
                :status="status"
                :can-operate="canOperate"
                :instrument="instrument"
                :instrument-status="instrumentStatus"
                :is-customer-blocked="isCustomerBlocked"
                :loading-button="loadingOrder"
                :description="description"
                @on-accept-risk-terms="emitAcceptRiskTerms"
                @confirm-order="confirmOrder"
                @on-order-button-disabled="sendOrderButtonDisabledLog"
              />
            </div>
          </TabPanel>
        </TabPanels>
      </Tabs>
      <ConfirmOrder
        v-if="action === OrderSteps.Confirmation"
        :balance="balanceUser"
        :instrument-qtd="qty"
        :type="getOrderType"
        :best-offer="value"
        :estimated-total="estimatedTotal"
        :brokerage-fee="brokerageFee"
        :brokerage-disclaimer="brokerageDisclaimer"
        :loading-button="loadingOrder"
        :instrument-title="instrumentTitle"
        :instrument="instrument"
        :position-quantity="quantityInstrument"
        :description="description"
        @request-order="createOrder"
        @back-to-instrument="onBackInstrument"
      />
      <div v-if="action === OrderSteps.Feedback">
        <div>
          <div class="instrument-status-heading">
            <div class="instrument-status-heading__button">
              <IconButton
                class="instrument-status-heading__button__icon"
                data-testid="confirm-order-arrow-icon"
                icon-name="EA0030"
                @click="onBackInstrument"
              />
            </div>
            <p class="text-p6-semibold instrument-status-heading__text">
              Confirmação
            </p>
          </div>
          <StatusIntrumentV2
            class="instrument-status-v2"
            :order-status="orderStatus"
            :title="statusMessage.title"
            :description="statusMessage.description"
            @click="redirectToOrders"
          />
        </div>
      </div>
    </div>
    <div v-if="action === OrderSteps.Form" class="siderbar-order_line" />
    <InstrumentDetails v-if="action === OrderSteps.Form" :ticker="ticker" />
    <OrderErrorModal :is-open="isError" @close="closeModal" />
  </div>
</template>

<script lang="ts">
import { Component, Prop, Watch, Emit } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import { namespace } from 'vuex-class';

import { RouteMixin } from '@/modules/common/mixins/route';
import { InstrumentDetails } from './components/InstrumentDetails';
import {
  ETradeDirection,
  ETradeOrderStatus,
  TradeOrderStatusMessage
} from '@/modules/trade/types/instrument';
import {
  Tab,
  Tabs,
  TabPanel,
  IconButton,
  TabPanels,
  TabList,
  HelperViewContextual
} from '@warrenbrasil/nebraska-web';
import { Order, OrderType } from './components/Order';
import { StatusIntrumentV2 } from './components/StatusIntrumentV2';
import { ConfirmOrder } from './components/ConfirmOrder';
import { OrderErrorModal } from './components/OrderErrorModal';

import { OrderSteps } from './types';
import { IInstrumentStatus } from '@/modules/trade/services/signalr';
import {
  createOrder,
  OrderDirection,
  InstrumentStatus,
  validateOrder
} from '@/modules/trade/services/instruments';
import { OrderStatus } from '@/modules/trade/types';
import {
  trackOrderConfirmationClick,
  trackChangeOrderTabClick
} from '@/modules/trade/views/Instrument/trackers';
import { AvailableBalanceData } from '@/modules/trade/services/unified-account/types';
import { OrderFiltersQueryParamsKeys } from '@/modules/trade/views/Orders/types';
import { throttle } from 'lodash-es';
import { datadogLogs } from '@datadog/browser-logs';
import { AccountStatus } from '@/modules/trade/services/account';
import { IDynamicContent } from '@/modules/common/types';

const tradePositionsModule = namespace('tradeModule');
const CustomerModule = namespace('CustomerModule');

@Component({
  components: {
    Tabs,
    TabList,
    Tab,
    IconButton,
    TabPanels,
    TabPanel,
    Order,
    ConfirmOrder,
    InstrumentDetails,
    OrderErrorModal,
    StatusIntrumentV2,
    HelperViewContextual
  }
})
export default class InstrumentSidebar extends mixins(RouteMixin) {
  @tradePositionsModule.State('userHasBrokerAccount')
  readonly userHasBrokerAccount!: boolean;

  @CustomerModule.Getter('isRegulatoryStatusBlocked')
  readonly isCustomerBlocked!: boolean;

  @Prop({ type: Number, required: true })
  readonly bestBuyPrice!: number;

  @Prop({ type: Number, required: true })
  readonly bestSellPrice!: number;

  @Prop({ type: Number, required: true })
  readonly lotSize!: number;

  @Prop({ type: Number, required: true })
  readonly quantityInstrument!: number;

  @Prop({ type: String, required: true })
  readonly instrumentTitle!: string;

  @Prop({ type: String, required: true })
  readonly ticker!: string;

  @Prop({ type: Boolean, required: false })
  readonly canOperate!: boolean;

  @Prop({ type: Object })
  readonly instrumentStatus!: IInstrumentStatus;

  @Prop({ type: Object, required: true })
  readonly status!: AccountStatus;

  @Prop({ type: Object, required: true })
  readonly instrument!: InstrumentStatus;

  @Prop({ type: String, default: '' })
  readonly description!: string;

  private currentTabIndex = 0;
  private OrderType = OrderType;
  public loadingOrder = false;
  private isError = false;
  private qty = 0;
  private estimatedTotal = 0;
  private value = 0;
  private brokerageFee = '';
  private brokerageDisclaimer: IDynamicContent[] = [];
  private direction: ETradeDirection | OrderDirection = ETradeDirection.Buy;
  private statusMessage?: TradeOrderStatusMessage | null;
  private orderStatus?: ETradeOrderStatus | OrderStatus | null;
  private action = OrderSteps.Form;
  readonly OrderSteps = OrderSteps;

  availableBalanceDataUnifiedAccount!: AvailableBalanceData;

  get computedOrderFormStyle() {
    return {
      display: this.action === 'ORDER_FORM' ? 'block' : 'none'
    };
  }

  get balanceUser() {
    return this.availableBalanceDataUnifiedAccount?.availableToOperateValue
      ?.formatted;
  }

  @Emit('on-accept-risk-terms')
  emitAcceptRiskTerms() {}

  onLoadUserAvailableBalanceUnifiedAccount(
    availableBalance: AvailableBalanceData
  ) {
    this.availableBalanceDataUnifiedAccount = availableBalance;
  }

  private async createOrder() {
    this.loadingOrder = true;
    trackOrderConfirmationClick(
      this.getOrderType,
      this.ticker,
      this.description,
      this.instrument.type!.formatted!
    );

    try {
      await this.createOrderBFF();
    } catch {
      this.isError = true;
    } finally {
      this.loadingOrder = false;
    }
  }

  private async createOrderBFF() {
    const body = {
      symbol: this.ticker,
      orderDirection: this.direction as unknown as OrderDirection,
      quantity: this.qty,
      price: this.value
    };
    const result = await createOrder(body);

    this.statusMessage = result.statusMessage;
    this.orderStatus = result.status.value;
    this.action = OrderSteps.Feedback;
  }

  sendOrderButtonDisabledLog = throttle((message: string) => {
    datadogLogs.logger.info('order-button-disabled', {
      reason: message,
      ticker: this.ticker
    });
  }, 60000);

  private redirectToOrders() {
    this.$router.push({
      name: 'orders',
      query: {
        [OrderFiltersQueryParamsKeys.Instrument]: this.ticker
      }
    });
  }

  private async confirmOrder(quantity: number) {
    this.loadingOrder = true;
    this.isError = false;

    try {
      const orderDirection: OrderDirection =
        this.getOrderType === OrderType.Sell
          ? OrderDirection.Sell
          : OrderDirection.Buy;

      const response = await validateOrder({
        quantity,
        orderDirection,
        symbol: this.ticker
      });

      this.qty = response.order.quantity;
      this.estimatedTotal = response.order.orderTotal.value;
      this.value = response.order.price.value;
      this.brokerageFee = response.order.brokerageFee.formatted;
      this.brokerageDisclaimer = response.order.brokerageDisclaimer;

      this.action = OrderSteps.Confirmation;
    } catch (error) {
      this.isError = true;
    } finally {
      this.loadingOrder = false;
    }
  }

  private onBackInstrument() {
    this.action = OrderSteps.Form;
  }

  private get getOrderType() {
    return this.currentTabIndex === OrderType.Buy
      ? OrderType.Buy
      : OrderType.Sell;
  }

  @Watch('currentTabIndex')
  private changeOrderDirection() {
    this.direction =
      this.currentTabIndex === 0 ? ETradeDirection.Buy : ETradeDirection.Sell;

    trackChangeOrderTabClick(this.direction, this.ticker);
  }

  private closeModal() {
    this.isError = false;
    this.action = OrderSteps.Form;
  }
}
</script>

<style lang="less" scoped>
.tabs {
  display: flex;
  flex-direction: row;
}

.siderbar-order_line {
  border-top: 1px solid @divider-primary;
}

.instrument-sidebar__container {
  width: 100%;
  height: 100%;
  background: @background-primary;
}

.instrument-status-v2 {
  margin-top: @size-spacing-x900;
}

.instrument-status-heading {
  margin-top: @size-spacing-x600;
  margin-bottom: @size-spacing-x500;
  position: relative;

  &__text {
    text-align: center;
    color: @text-primary;
  }

  &__button {
    height: 100%;
    position: absolute;
    cursor: pointer;
    display: flex;

    &__icon {
      align-self: center;
    }
  }
}

@media only screen and (min-width: 769px) {
  .instrument-sidebar__container {
    width: 400px;
    background: @background-secondary;
    border-left: 1px solid @divider-primary;
  }
}
</style>
